import {
  BABY_DISCHARGE_LABEL,
  isBabyDischargeSummary,
} from "features/results/utils/neonatalDischarge";

/*
 * "All Documents" is the one section that has to read as one list (KIN-2068).
 *
 * Uploaded documents come from the documents endpoint; discharge summaries (KIN-2028) and DEASP
 * certificates each have their own endpoint and their own row shape. Before this, All asked only
 * the documents endpoint, so a patient whose only document was a hospital discharge summary was
 * told "No documents found" — a clinical document one click away, presented as nothing.
 *
 * Rather than a second table for All, each foreign row is mapped onto the uploaded-document row
 * shape the table already renders, and keeps:
 *   `source`    which endpoint it came from — what the row's cells and actions key off;
 *   `sourceRow` the untouched original, handed straight back to that section's own viewer, so
 *               All opens the same modal rather than a parallel rendering of the document.
 */

export const DOCUMENT_ROW_SOURCES = {
  upload: "UPLOAD",
  dischargeSummary: "DISCHARGE_SUMMARY",
  deasp: "DEASP",
};

export const DISCHARGE_SUMMARY_ROW_LABEL = "Discharge summary";
export const SICK_CERTIFICATE_ROW_LABEL = "Sick certificate";

const DEASP_CERTIFICATE_LABELS = {
  MED1: "Medical Certificate 1",
  MED2: "Medical Certificate 2",
};

/*
 * An uploaded document carries no `source` — it is the shape everything else is mapped onto, and
 * it is also what every other section of the page passes in. Absent therefore means uploaded.
 */
export const isUploadedDocumentRow = (row) =>
  (row?.source ?? DOCUMENT_ROW_SOURCES.upload) === DOCUMENT_ROW_SOURCES.upload;

export const isDeaspCertificateRow = (row) =>
  row?.source === DOCUMENT_ROW_SOURCES.deasp;

/*
 * Ids collide across endpoints: a document and a result can both be id 7. The row id drives
 * selection and React keys, so foreign rows are namespaced. Uploaded ids stay untouched, because
 * every existing handler (delete, change status, bulk email) sends them to the backend as they are.
 */
export const toDischargeSummaryRow = (summary) => {
  // KIN-1949: a neonatal summary sits on the mother's file. Dropping that distinction here would
  // list her baby's discharge indistinguishably from her own — the exact bug that ticket fixed.
  const label = isBabyDischargeSummary(summary)
    ? BABY_DISCHARGE_LABEL
    : DISCHARGE_SUMMARY_ROW_LABEL;

  return {
    id: `discharge-summary-${summary.id}`,
    source: DOCUMENT_ROW_SOURCES.dischargeSummary,
    name: label,
    documentTypeLabel: label,
    /*
     * Mapped onto `created` so the merged list sorts on one field with no date arithmetic of its
     * own: the existing sort already parses "YYYY-MM-DD HH:mm:ss" alongside the documents
     * endpoint's own formats.
     */
    created: summary.orderDate ?? null,
    sourceRow: summary,
  };
};

export const toDeaspCertificateRow = (certificate) => {
  const certificateDetails = certificate.deaspCertificate;

  return {
    id: `deasp-${certificate.id}`,
    source: DOCUMENT_ROW_SOURCES.deasp,
    name:
      DEASP_CERTIFICATE_LABELS[certificateDetails?.certFormType] ??
      SICK_CERTIFICATE_ROW_LABEL,
    documentTypeLabel: SICK_CERTIFICATE_ROW_LABEL,
    /*
     * The certificate's own date. The endpoint names it `createdAt` and populates it on every
     * record (KIN-2102) — without it the chain fell through to `sickFromDate`, which left Date
     * Uploaded blank on every cert that had no sick-from date. `created`/`dateCreated` are kept
     * ahead of the clinical fallback in case an older payload carries either.
     */
    created:
      certificate.createdAt ??
      certificate.created ??
      certificate.dateCreated ??
      certificateDetails?.sickFromDate ??
      null,
    /*
     * The submission status — PENDING, CERT_ACCEPTED, ERROR, DEASP_ERROR and the rest. Projected
     * onto the row under its own name rather than onto `status`, which on this table means the
     * review workflow an uploaded document goes through: the two are different things, and the
     * page's status filter and sort both read `status`.
     */
    deaspStatus: certificate.status ?? null,
    sourceRow: certificate,
  };
};

/*
 * One array for the table. The page's existing filter/sort hook then orders it — newest first by
 * default — across all three sources, and a row whose source carries no usable date sorts last
 * rather than being dropped: an undated document is still a document the GP must be able to see.
 */
export const mergeAllDocumentRows = ({
  uploads = [],
  dischargeSummaries = [],
  deaspCertificates = [],
}) => [
  ...uploads,
  ...dischargeSummaries.map(toDischargeSummaryRow),
  ...deaspCertificates.map(toDeaspCertificateRow),
];
